import { type InfiniteData, mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type {
    GenerateSessionInput,
    GetSessionListOutput,
    ScheduleSessionInput,
    Session,
    SessionAction,
    SessionListItem,
    UpdateSessionInput,
} from '../model/session.types';
import { sessionsApi } from './session.api';
import { queryKeys } from './session.queries';

export const mutationKeys = {
    all: ['sessions'] as const,
    session: (sessionId: string) => [...mutationKeys.all, 'session', sessionId] as const,
    updateSession: (sessionId: string) => [...mutationKeys.session(sessionId), 'update'] as const,
};

export function useScheduleSessionOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (data: ScheduleSessionInput) => sessionsApi.schedule(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.all }),
        onError: () => toast.error('Session was not created'),
    });
}

export function useGenerateSessionOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (command: GenerateSessionInput) => sessionsApi.generate(command),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.all });
        },
        onError: (error) => {
            console.error('Failed to generate session:', error);
            toast.error('Failed to save session');
        },
    });
}

export function useDeleteSessionOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (sessionId: string) => sessionsApi.delete(sessionId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.all });
            toast.success('Session deleted');
        },
        onError: (error) => {
            console.error('Failed to delete session:', error);
            toast.error('Failed to delete session');
        },
    });
}

export function useUpdateSessionOptions(sessionId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.updateSession(sessionId),
        mutationFn: (command: UpdateSessionInput) => sessionsApi.update(command),
        onMutate: async (newData) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.detail(sessionId) });

            const previousSession = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            if (previousSession) {
                const updates: Partial<Session> = {};

                if (newData.treatmentApproaches) {
                    updates.treatmentApproaches = newData.treatmentApproaches;
                }

                if (newData.status) {
                    updates.status = newData.status;
                }

                if (Object.keys(updates).length > 0) {
                    queryClient.setQueryData<Session>(queryKeys.detail(sessionId), {
                        ...previousSession,
                        ...updates,
                    });
                }
            }

            return { previousSession };
        },
        onError: (error, _newData, context) => {
            if (context?.previousSession) {
                queryClient.setQueryData(queryKeys.detail(sessionId), context.previousSession);
            }
            console.error('Failed to update session:', error);
            toast.error('Failed to update session');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.detail(sessionId) });
        },
    });
}

export function useResolveActionOptions(sessionId: string, action: SessionAction) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: () =>
            sessionsApi.resolveAction({
                id: sessionId,
                action,
            }),
        onMutate: async () => {
            const prevDetails = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            if (prevDetails) {
                queryClient.setQueryData(queryKeys.detail(sessionId), {
                    ...prevDetails,
                    actions: prevDetails.actions.filter((a) => a !== action),
                });
            }

            return { prevDetails };
        },
        onSuccess: () => {
            queryClient.setQueriesData(
                {
                    queryKey: queryKeys.lists(),
                },
                (infiniteData: InfiniteData<GetSessionListOutput>) => {
                    if (!infiniteData?.pages.length) return infiniteData;

                    return {
                        ...infiniteData,
                        pages: infiniteData.pages.map((page) => ({
                            ...page,
                            items: page.items.map((session) => {
                                if (session.id !== sessionId) {
                                    return session;
                                }

                                return {
                                    ...session,
                                    actions: session?.actions.filter((a) => a !== action),
                                };
                            }),
                        })),
                    };
                },
            );
            queryClient.setQueriesData<SessionListItem[]>(
                {
                    queryKey: queryKeys.days(),
                },
                (sessions) => {
                    if (!sessions || !sessions.length) return sessions;

                    return sessions.map((session) => {
                        if (session.id !== sessionId) {
                            return session;
                        }

                        return {
                            ...session,
                            actions: session?.actions.filter((a) => a !== action),
                        };
                    });
                },
            );
        },
        onError: (_, __, context) => {
            if (context?.prevDetails) {
                queryClient.setQueryData(queryKeys.detail(sessionId), context.prevDetails);
            }
        },
    });
}
